import { MovementPhase } from './movementData'
// Subsistence status types based on ration percentage
export type SubsistenceStatus = 'green' | 'amber' | 'red' | 'black'
// Interface for subsistence nodes
export interface SubsistenceNode {
  id: string
  name: string
  label: string
  coordinates: [number, number] // [latitude, longitude]
  description: string
  isAlternate: boolean // Whether this is an alternate node
}
// Interface for subsistence distribution corridors
export interface SubsistenceDistributionCorridor {
  id: string
  name: string
  coordinates: [number, number][] // Array of coordinate pairs forming a route
  description: string
  nodeId: string // ID of the subsistence node this corridor is connected to
}
// Interface for daily subsistence status
export interface DailySubsistenceStatus {
  day: number
  percentageOnHand: number
  status: SubsistenceStatus
  notes: string
  waterDiscipline?: 'normal' | 'active' | 'critical'
}
// Status colors for visual representation
export const subsistenceStatusColors: Record<SubsistenceStatus, string> = {
  green: '#059669', // Green
  amber: '#D97706', // Amber
  red: '#DC2626', // Red
  black: '#1F2937', // Black
}
// Get subsistence status based on percentage
export const getSubsistenceStatus = (percentage: number): SubsistenceStatus => {
  if (percentage >= 75) return 'green'
  if (percentage >= 50) return 'amber'
  if (percentage >= 25) return 'red'
  return 'black'
}
// Subsistence nodes data (using the same locations as fuel nodes)
export const subsistenceNodes: SubsistenceNode[] = [
  {
    id: 'fort-stockton',
    name: 'F1 - Fort Stockton Class I Supply Point',
    label: 'F1',
    coordinates: [30.8941, -102.8793], // Fort Stockton
    description: 'Class I rations depot. Capable of supporting full reloads.',
    isAlternate: false,
  },
  {
    id: 'alpine',
    name: 'F1 (Alt) - Alpine Class I Supply Point',
    label: 'F1-ALT',
    coordinates: [30.3584, -103.6614], // Alpine
    description: 'Alternate provisioning point for rations and potable water.',
    isAlternate: true,
  },
]
// Subsistence distribution corridors
export const subsistenceDistributionCorridors: SubsistenceDistributionCorridor[] =
  [
    {
      id: 'rd1-stockton',
      name: 'RD1 - Fort Stockton to Del Rio',
      coordinates: [
        [30.8941, -102.8793], // Fort Stockton
        [30.1422, -102.3979], // Sanderson (intermediate point)
        [29.3627, -100.8968], // Del Rio
      ],
      description: 'Primary ration distribution corridor',
      nodeId: 'fort-stockton',
    },
    {
      id: 'rd1-alpine',
      name: 'RD1 - Alpine to Del Rio',
      coordinates: [
        [30.3584, -103.6614], // Alpine
        [30.1422, -102.3979], // Sanderson (intermediate point)
        [29.3627, -100.8968], // Del Rio
      ],
      description: 'Alternate ration distribution corridor',
      nodeId: 'alpine',
    },
  ]
// Daily subsistence status data
export const dailySubsistenceStatus: DailySubsistenceStatus[] = [
  {
    day: 1,
    percentageOnHand: 81,
    status: 'green',
    notes: 'Full initial provisioning',
    waterDiscipline: 'normal',
  },
  {
    day: 2,
    percentageOnHand: 65,
    status: 'amber',
    notes: 'Moderate usage',
    waterDiscipline: 'normal',
  },
  {
    day: 3,
    percentageOnHand: 49,
    status: 'amber',
    notes: 'Monitor closely',
    waterDiscipline: 'active',
  },
  {
    day: 4,
    percentageOnHand: 33,
    status: 'red',
    notes: 'Low – prep for resupply',
    waterDiscipline: 'active',
  },
  {
    day: 5,
    percentageOnHand: 100,
    status: 'green',
    notes: 'Resupply complete',
    waterDiscipline: 'normal',
  },
  {
    day: 6,
    percentageOnHand: 84,
    status: 'green',
    notes: 'Post-resupply drawdown begins',
    waterDiscipline: 'normal',
  },
  {
    day: 7,
    percentageOnHand: 68,
    status: 'amber',
    notes: 'Stable for end-of-mission, re-eval needed',
    waterDiscipline: 'active',
  },
]
// Helper function to get subsistence status for a specific day
export const getSubsistenceStatusForDay = (
  day: number,
): DailySubsistenceStatus => {
  return (
    dailySubsistenceStatus.find((status) => status.day === day) ||
    dailySubsistenceStatus[0]
  )
}
// Helper function to convert time point to day number (reusing from other data files)
export const timePointToDay = (timePoint: string): number => {
  switch (timePoint) {
    case '24h':
      return 1
    case '48h':
      return 2
    case '72h':
      return 3
    case '96h':
      return 4
    case '5d':
      return 5
    case '6d':
      return 6
    case '7d':
      return 7
    default:
      return 0 // For "now", show day 0 (not started)
  }
}
